/* eslint-disable react-refresh/only-export-components -- context module
   intentionally co-exports the CityProvider component and the useCity hook. */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { listCities } from '../api/cityApi';
import { FIXED_PROJECT_ID } from '../constants/projectConstants';

/**
 * CityContext — the dashboard's multi-city scope.
 *
 * Documented deviation from CLAUDE.md's "no Context" rule: the active city
 * (projectId) is read at ~63 call sites across ~13 components; a single context
 * is far cleaner than prop-drilling. Components read the active city via
 * getActiveProjectId() (localStorage), which this provider keeps in sync.
 *
 * Allowed cities come from the logged-in user: a normal user sees only the
 * cities in their `cities` list (stored at login); a super-admin sees all.
 * Switching the city reloads the app so every component refetches under the
 * new scope without per-effect dependency wiring.
 */
const CityContext = createContext(null);

export function useCity() {
  const ctx = useContext(CityContext);
  if (!ctx) throw new Error('useCity must be used within <CityProvider>');
  return ctx;
}

function readAuthCities() {
  try {
    const raw = localStorage.getItem('userCities');
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function CityProvider({ children }) {
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const [cities, setCities] = useState([]); // [{ projectId, displayName }]
  const [currentCity, setCurrentCityState] = useState(
    () => localStorage.getItem('currentCity') || null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await listCities({ status: 'active' });
      const allowedIds = readAuthCities();
      const allowed = isSuperAdmin
        ? all
        : all.filter((c) => allowedIds.includes(c.projectId));
      setCities(allowed);
      setCurrentCityState((prev) => {
        const saved = localStorage.getItem('currentCity');
        const pick =
          (prev && allowed.some((c) => c.projectId === prev) && prev) ||
          (saved && allowed.some((c) => c.projectId === saved) && saved) ||
          (allowed[0] && allowed[0].projectId) ||
          null;
        if (pick) localStorage.setItem('currentCity', pick);
        return pick;
      });
    } catch (err) {
      setError(err.message || 'Failed to load cities');
      // Fallback: if the registry can't load but we know the user's city ids,
      // use them raw (projectId as label) so the app still scopes correctly.
      const allowedIds = readAuthCities();
      if (allowedIds.length) {
        setCities(allowedIds.map((id) => ({ projectId: id, displayName: id })));
        setCurrentCityState((prev) => {
          const pick = prev || allowedIds[0];
          localStorage.setItem('currentCity', pick);
          return pick;
        });
      }
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadCities();
  }, [loadCities]);

  // Switching city reloads so every tab refetches under the new scope.
  const setCurrentCity = useCallback((projectId) => {
    if (!projectId || projectId === localStorage.getItem('currentCity')) return;
    localStorage.setItem('currentCity', projectId);
    setCurrentCityState(projectId);
    if (typeof window !== 'undefined') window.location.reload();
  }, []);

  const value = useMemo(
    () => ({
      currentCity: currentCity || FIXED_PROJECT_ID,
      setCurrentCity,
      cities,
      isSuperAdmin,
      loading,
      error,
      reloadCities: loadCities,
    }),
    [currentCity, setCurrentCity, cities, isSuperAdmin, loading, error, loadCities]
  );

  return <CityContext.Provider value={value}>{children}</CityContext.Provider>;
}
